import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import { useStripes } from '@folio/stripes/core';
import { flatMap, isEmpty, chunk, isEqual } from 'lodash';
import { ErrorDetailsModal } from '../../../../shared/ErrorHandling/components';
import useTenantKy from '../../../../hooks/useTenantKy';
import {
  getHoldingsStatus,
  getItemsStatus,
  setHoldingPostPayload,
  setInstancePayload,
  setHoldingPutPayload,
  setItemPostPayload,
  setItemPutPayload,
} from '../../../../utils/utils';
import { BarcodeLastIndexContext } from '../../../../contexts';
import OnSubmitModal from './OnSubmitModal';

// Batch processing configuration - optimized for speed
const BATCH_CONFIG = {
  SMALL_DATASET: { batchSize: 15, delay: 200 }, // Less than 50 items (increased size, reduced delay)
  MEDIUM_DATASET: { batchSize: 12, delay: 300 }, // 50-200 items
  LARGE_DATASET: { batchSize: 8, delay: 400 }, // More than 200 items (reduced delay)
};

// Delay between requests
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Extract detailed error message from API response
const extractErrorMessage = async (error) => {
  let errorText = error.message; // Default message

  if (error.response) {
    try {
      const contentType = error.response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        // Clone the response to avoid consuming the stream
        const responseClone = error.response.clone();
        const errorData = await responseClone.json();

        // FOLIO API usually sends errors in one of these formats:
        errorText =
          errorData.errors?.[0]?.message ||
          errorData.error ||
          errorData.message ||
          errorData.errorMessage ||
          errorData.msg ||
          errorData.detail ||
          errorData.title ||
          JSON.stringify(errorData);

        // If still generic, try to get more specific info
        if (errorText === 'Bad Request' && errorData.errors?.[0]) {
          const firstError = errorData.errors[0];
          errorText = firstError.code
            ? `${firstError.code}: ${firstError.message || firstError.parameters || 'Bad Request'}`
            : firstError.message || firstError.parameters || 'Bad Request';
        }
      } else {
        // If response is plain text
        const responseClone = error.response.clone();
        errorText = await responseClone.text();
      }

      // Enhanced error code handling
      if (
        error.response.status &&
        (errorText === 'Bad Request' || errorText.trim() === '')
      ) {
        errorText = `HTTP ${error.response.status}: ${error.response.statusText || 'Bad Request'} - Check the data format`;
      }
    } catch (e) {
      // If extraction fails, provide more detailed fallback
      console.warn('Failed to extract error message:', e);
      errorText = `HTTP ${error.response.status || 'Unknown'}: ${error.response.statusText || error.message}`;
    }
  }

  return errorText;
};

// Message component
function createMessage(text, type) {
  return React.createElement(
    'div',
    {
      style: {
        padding: '8px 12px',
        borderRadius: '4px',
        margin: '4px 0',
        backgroundColor:
          type === 'success'
            ? '#d4edda'
            : type === 'error'
              ? '#f8d7da'
              : '#d1ecf1',
        border: `1px solid ${type === 'success' ? '#c3e6cb' : type === 'error' ? '#f5c6cb' : '#bee5eb'}`,
        color:
          type === 'success'
            ? '#155724'
            : type === 'error'
              ? '#721c24'
              : '#0c5460',
      },
    },
    text,
  );
}

function OnSubmitProgressModal({
  onClose,
  onSuccess,
  open,
  instanceId,
  formValues,
  initialValues,
  instance,
  holdings,
  items,
}) {
  const {
    okapi: { tenant },
  } = useStripes();
  const ky = useTenantKy({ tenantId: tenant });
  const { configId, barcodeLastIndex } = useContext(BarcodeLastIndexContext);

  // Message and progress states
  const [instanceProgressMsg, setInstanceProgressMsg] = useState([]);
  const [holdingsProgressMsg, setHoldingsProgressMsg] = useState([]);
  const [itemsProgressMsg, setItemsProgressMsg] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  // Operation states
  const [totalProcesses, setTotalProcesses] = useState(0);
  const [processesSummary, setProcessesSummary] = useState([]);
  const [processCounter, setProcessCounter] = useState(0);
  const [startUpdate, setStartUpdate] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [showErrors, setShowErrors] = useState(false); // Add error display state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [optimisticLockError, setOptimisticLockError] = useState(null); // Add optimistic locking check

  const barcodeUpdateCountRef = useRef(0);

  // Check for optimistic locking conflicts before starting update
  const checkForVersionConflicts = useCallback(async () => {
    try {
      // Check current Instance version
      const currentInstance = await ky
        .get(`inventory/instances/${instanceId}`)
        .json();
      if (currentInstance._version !== instance._version) {
        return {
          type: 'instance',
          message: 'Instance has been modified by another user',
          latestVersionLink: `/inventory/view/${instanceId}`,
          details: `Stored version: ${currentInstance._version}, Current version: ${instance._version}`,
        };
      }

      // Check Holdings versions
      if (holdings && holdings.length > 0) {
        for (const holding of holdings) {
          try {
            const currentHolding = await ky
              .get(`holdings-storage/holdings/${holding.id}`)
              .json();
            if (currentHolding._version !== holding._version) {
              return {
                type: 'holdings',
                message: `Holding "${holding.hrid}" has been modified by another user`,
                latestVersionLink: `/inventory/view/${instanceId}`,
                details: `Stored version: ${currentHolding._version}, Current version: ${holding._version}`,
              };
            }
          } catch (error) {
            // If holding doesn't exist anymore, it's also a conflict
            if (error.response?.status === 404) {
              return {
                type: 'holdings',
                message: `Holding "${holding.hrid}" has been deleted by another user`,
                latestVersionLink: `/inventory/view/${instanceId}`,
              };
            }
          }
        }
      }

      return null; // No conflicts found
    } catch (error) {
      console.error('Error checking version conflicts:', error);
      return {
        type: 'error',
        message:
          'Unable to verify current version. Please refresh the page and try again.',
        details: error.message,
      };
    }
  }, [ky, instanceId, instance, holdings]);

  // Analysis functions
  const analyzeInstanceChanges = useCallback((initial, current) => {
    return !isEqual(initial.instanceData, current.instanceData);
  }, []);

  const analyzeHoldingsChanges = useCallback((initial, current) => {
    const initialData = initial.holdingsData || [];
    const currentData = current.holdingsData || [];

    const initialMap = new Map(initialData.map((h) => [h.id, h]));
    const currentMap = new Map(currentData.map((h) => [h.id, h]));

    const deleted = initialData.filter((h) => !currentMap.has(h.id));
    const added = currentData.filter((h) => !initialMap.has(h.id));
    const modified = currentData.filter(
      (h) => initialMap.has(h.id) && !isEqual(initialMap.get(h.id), h),
    );

    return {
      hasChanges: deleted.length > 0 || added.length > 0 || modified.length > 0,
      deleted,
      added,
      modified,
    };
  }, []);

  const analyzeItemsChanges = useCallback((initial, current) => {
    const initialData = initial.itemsData || {};
    const currentData = current.itemsData || {};

    // Flatten items from all holdings
    const initialItems = Object.values(initialData).flat();
    const currentItems = Object.values(currentData).flat();

    const initialMap = new Map(initialItems.map((i) => [i.id, i]));
    const currentMap = new Map(currentItems.map((i) => [i.id, i]));

    const deleted = initialItems.filter((i) => !currentMap.has(i.id));
    const added = currentItems.filter((i) => !initialMap.has(i.id));
    const modified = currentItems.filter(
      (i) => initialMap.has(i.id) && !isEqual(initialMap.get(i.id), i),
    );

    return {
      hasChanges: deleted.length > 0 || added.length > 0 || modified.length > 0,
      deleted,
      added,
      modified,
    };
  }, []);

  // Helper functions
  const addInstanceMessage = useCallback((text, type = 'info') => {
    setInstanceProgressMsg((prev) => [...prev, createMessage(text, type)]);
  }, []);

  const addHoldingsMessage = useCallback((text, type = 'info') => {
    setHoldingsProgressMsg((prev) => [...prev, createMessage(text, type)]);
  }, []);

  const addItemsMessage = useCallback((text, type = 'info') => {
    setItemsProgressMsg((prev) => [...prev, createMessage(text, type)]);
  }, []);

  const incrementCounter = useCallback(() => {
    setProcessCounter((prev) => prev + 1);
  }, []);

  // Determine optimal configuration based on data size
  const getOptimalConfig = useCallback((operationsCount) => {
    if (operationsCount <= 50) return BATCH_CONFIG.SMALL_DATASET;
    if (operationsCount <= 200) return BATCH_CONFIG.MEDIUM_DATASET;
    return BATCH_CONFIG.LARGE_DATASET;
  }, []);

  // Process operations in batches - optimized for speed
  const processBatch = useCallback(async (operations, config, processor) => {
    const batches = chunk(operations, config.batchSize);
    setTotalBatches(batches.length);
    const results = { successful: 0, failed: 0 };

    for (let i = 0; i < batches.length; i++) {
      setCurrentBatch(i + 1);
      const batch = batches[i];

      // Parallel processing with concurrency control
      const concurrencyLimit = Math.min(3, batch.length); // Maximum 3 concurrent operations
      const chunks = chunk(batch, concurrencyLimit);

      for (const subChunk of chunks) {
        // Use allSettled to continue even if some operations fail
        const settledResults = await Promise.allSettled(
          subChunk.map((operation) => processor(operation)),
        );

        // Count successes and failures
        settledResults.forEach((result) => {
          if (
            result.status === 'fulfilled' &&
            result.value?.success !== false
          ) {
            results.successful++;
          } else {
            results.failed++;
            if (result.status === 'rejected') {
              console.error('Operation failed:', result.reason);
            }
          }
        });
      }

      // Reduced delay between batches
      if (i < batches.length - 1) {
        await delay(config.delay);
      }
    }

    return results;
  }, []);

  // Update barcode configuration
  const updateBarcodeConfig = useCallback(async () => {
    if (barcodeUpdateCountRef.current === 0) return;

    try {
      await ky.put(`inventory-templates-configs/${configId}`, {
        json: {
          configName: 'barcodeLastIndex',
          configCode: 'barcodeLastIndex',
          configValue: {
            lastIndex: barcodeLastIndex + barcodeUpdateCountRef.current,
          },
        },
      });

      addItemsMessage(
        <FormattedMessage
          id="ui-plugin-inventory-templates.barcode.configUpdated"
          defaultMessage="Barcode configuration updated successfully. New last index: {lastIndex}"
          values={{
            lastIndex: barcodeLastIndex + barcodeUpdateCountRef.current,
          }}
        />,
        'success',
      );
      barcodeUpdateCountRef.current = 0;
    } catch (error) {
      console.error('Barcode update error:', error);
    }
  }, [ky, configId, barcodeLastIndex, addItemsMessage]);

  // Process Items
  const processItems = useCallback(
    async (itemsToProcess, operation, config) => {
      if (!itemsToProcess || itemsToProcess.length === 0) return;

      const processor = async (item) => {
        try {
          let result;

          if (operation === 'create') {
            result = await ky.post('inventory/items', {
              json: setItemPostPayload(item),
            });
            barcodeUpdateCountRef.current++;
            addItemsMessage(
              <FormattedMessage
                id="ui-plugin-inventory-templates.items.createSuccess"
                defaultMessage="Created item: {identifier}"
                values={{
                  identifier:
                    item.barcode || item.itemHrid || item.hrid || item.id,
                }}
              />,
              'success',
            );
          } else if (operation === 'update') {
            const originalItem = items[item.holdingsRecordId]?.find(
              (i) => i.id === item.id,
            );

            result = await ky.put(`inventory/items/${item.id}`, {
              json: setItemPutPayload(item, originalItem),
            });
            if (item.barcode !== originalItem?.barcode) {
              barcodeUpdateCountRef.current++;
            }
            addItemsMessage(
              <FormattedMessage
                id="ui-plugin-inventory-templates.items.updateSuccess"
                defaultMessage="Updated item: {identifier}"
                values={{
                  identifier:
                    item.barcode || item.itemHrid || item.hrid || item.id,
                }}
              />,
              'success',
            );
          } else if (operation === 'delete') {
            result = await ky.delete(`inventory/items/${item.id}`);
            addItemsMessage(
              <FormattedMessage
                id="ui-plugin-inventory-templates.items.deleteSuccess"
                defaultMessage="Deleted item: {identifier}"
                values={{
                  identifier:
                    item.barcode || item.itemHrid || item.hrid || item.id,
                }}
              />,
              'success',
            );
          }

          incrementCounter();
          return result;
        } catch (error) {
          incrementCounter();
          // Extract detailed error message
          const errorText = await extractErrorMessage(error);
          addItemsMessage(
            <FormattedMessage
              id={`ui-plugin-inventory-templates.items.${operation}Error`}
              defaultMessage={`Failed to ${operation} item`}
            />,
            'error',
          );
          setErrorMessages((prev) => [
            ...prev,
            {
              category: 'items',
              operation: `${operation} item ${item.barcode || item.hrid || item.id}`,
              message: errorText,
              timestamp: new Date().toISOString(),
              code: error.response?.status || 'UNKNOWN',
            },
          ]);
          // Don't throw - allow other items to be processed
          return { success: false, error, item };
        }
      };

      await processBatch(itemsToProcess, config, processor);
    },
    [ky, items, addItemsMessage, incrementCounter, processBatch],
  );

  // Process Holdings
  const processHoldings = useCallback(
    async (holdingsToProcess, operation, config) => {
      if (!holdingsToProcess || holdingsToProcess.length === 0) return;

      const processor = async (holding) => {
        try {
          let result;

          if (operation === 'create') {
            result = await ky.post('holdings-storage/holdings', {
              json: setHoldingPostPayload(holding, formValues),
            });
            addHoldingsMessage(
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdings.createSuccess"
                defaultMessage="Created holding: {identifier}"
                values={{
                  identifier: holding.holdingHrid || holding.hrid || holding.id,
                }}
              />,
              'success',
            );
          } else if (operation === 'update') {
            const originalHolding = holdings.find((h) => h.id === holding.id);
            const { holdingsItems, bareHoldingsItems, ...rest } =
              originalHolding || {};

            result = await ky.put(`holdings-storage/holdings/${holding.id}`, {
              json: setHoldingPutPayload(holding, rest),
            });
            addHoldingsMessage(
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdings.updateSuccess"
                defaultMessage="Updated holding: {identifier}"
                values={{
                  identifier: holding.holdingHrid || holding.hrid || holding.id,
                }}
              />,
              'success',
            );
          } else if (operation === 'delete') {
            result = await ky.delete(`holdings-storage/holdings/${holding.id}`);
            addHoldingsMessage(
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdings.deleteSuccess"
                defaultMessage="Deleted holding: {identifier}"
                values={{
                  identifier: holding.holdingHrid || holding.hrid || holding.id,
                }}
              />,
              'success',
            );
          }

          incrementCounter();
          return result;
        } catch (error) {
          incrementCounter();
          // Extract detailed error message
          const errorText = await extractErrorMessage(error);
          addHoldingsMessage(
            <FormattedMessage
              id={`ui-plugin-inventory-templates.holdings.${operation}Error`}
              defaultMessage={`Failed to ${operation} holding`}
            />,
            'error',
          );
          setErrorMessages((prev) => [
            ...prev,
            {
              category: 'holdings',
              operation: `${operation} holding ${holding.hrid || holding.id}`,
              message: errorText,
              timestamp: new Date().toISOString(),
              code: error.response?.status || 'UNKNOWN',
            },
          ]);
          // Don't throw - allow other holdings to be processed
          return { success: false, error, holding };
        }
      };

      await processBatch(holdingsToProcess, config, processor);
    },
    [
      ky,
      holdings,
      formValues,
      items,
      addHoldingsMessage,
      incrementCounter,
      processBatch,
      processItems,
    ],
  );

  // Main process execution
  const executeMainProcess = useCallback(async () => {
    if (!startUpdate || isProcessing) return;

    setIsProcessing(true);
    let instanceUpdated = false;

    try {
      // Analyze changes using initial vs current values
      const instanceHasChanges = analyzeInstanceChanges(
        initialValues,
        formValues,
      );
      const holdingsAnalysis = analyzeHoldingsChanges(
        initialValues,
        formValues,
      );
      const itemsAnalysis = analyzeItemsChanges(initialValues, formValues);

      // Update Instance
      if (!instanceHasChanges) {
        addInstanceMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.instance.updateSkipped"
            defaultMessage="Instance update skipped - no changes detected"
          />,
          'success',
        );
        incrementCounter();
      } else {
        addInstanceMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.instance.updateStarting"
            defaultMessage="Starting instance update..."
          />,
          'info',
        );

        const instancePayload = setInstancePayload(
          formValues,
          instanceId,
          instance,
        );

        try {
          await ky.put(`inventory/instances/${instanceId}`, {
            json: instancePayload,
          });

          addInstanceMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.instance.updateSuccess"
              defaultMessage="Instance ({title}) updated successfully"
              values={{ title: instance.title }}
            />,
            'success',
          );
          incrementCounter();
          instanceUpdated = true;
        } catch (error) {
          // Handle instance update error specifically
          const errorText = await extractErrorMessage(error);
          addInstanceMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.instance.updateError"
              defaultMessage="Failed to update instance"
            />,
            'error',
          );
          setErrorMessages((prev) => [
            ...prev,
            {
              category: 'instance',
              operation: 'Update instance',
              message: errorText,
              timestamp: new Date().toISOString(),
              code: error.response?.status || 'UNKNOWN',
            },
          ]);
          // Don't continue if instance update failed
          return;
        }
      }

      // Process NEW Holdings FIRST (to provide holdingsRecordId for new items)
      const newHoldings = holdingsAnalysis.added;
      const remainingHoldings = [
        ...holdingsAnalysis.modified,
        ...holdingsAnalysis.deleted,
      ];

      if (newHoldings.length > 0 && (instanceUpdated || !instanceHasChanges)) {
        const config = getOptimalConfig(newHoldings.length);
        addHoldingsMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.holdings.createStarting"
            defaultMessage="Starting new holdings creation..."
          />,
          'info',
        );
        await processHoldings(newHoldings, 'create', config);
      }

      // Process ALL Items (after new holdings are created)
      if (!itemsAnalysis.hasChanges) {
        addItemsMessage(
          <FormattedMessage
            id="ui-plugin-inventory-templates.items.updateSkipped"
            defaultMessage="Items update skipped - no changes detected"
          />,
          'success',
        );
      } else if (instanceUpdated || !instanceHasChanges) {
        const totalItemsOps =
          itemsAnalysis.deleted.length +
          itemsAnalysis.added.length +
          itemsAnalysis.modified.length;
        const config = getOptimalConfig(totalItemsOps);

        // Execute items operations in order: delete → create → update
        if (itemsAnalysis.deleted.length) {
          addItemsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.items.deleteStarting"
              defaultMessage="Starting items deletion..."
            />,
            'info',
          );
          await processItems(itemsAnalysis.deleted, 'delete', config);
        }

        if (itemsAnalysis.added.length) {
          addItemsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.items.createStarting"
              defaultMessage="Starting items creation..."
            />,
            'info',
          );
          await processItems(itemsAnalysis.added, 'create', config);
        }

        if (itemsAnalysis.modified.length) {
          addItemsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.items.updateStarting"
              defaultMessage="Starting items update..."
            />,
            'info',
          );
          await processItems(itemsAnalysis.modified, 'update', config);
        }
      }

      // Process REMAINING Holdings (update/delete after items are processed)
      if (remainingHoldings.length === 0) {
        if (newHoldings.length === 0) {
          addHoldingsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.holdings.updateSkipped"
              defaultMessage="Holdings update skipped - no changes detected"
            />,
            'success',
          );
        }
      } else if (instanceUpdated || !instanceHasChanges) {
        const config = getOptimalConfig(remainingHoldings.length);
        const holdingsToUpdate = remainingHoldings.filter((h) =>
          holdingsAnalysis.modified.includes(h),
        );
        const holdingsToDelete = remainingHoldings.filter((h) =>
          holdingsAnalysis.deleted.includes(h),
        );

        if (holdingsToUpdate.length) {
          addHoldingsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.holdings.updateStarting"
              defaultMessage="Starting holdings update..."
            />,
            'info',
          );
          await processHoldings(holdingsToUpdate, 'update', config);
        }

        if (holdingsToDelete.length) {
          addHoldingsMessage(
            <FormattedMessage
              id="ui-plugin-inventory-templates.holdings.deleteStarting"
              defaultMessage="Starting holdings deletion..."
            />,
            'info',
          );
          await processHoldings(holdingsToDelete, 'delete', config);
        }
      }

      // Update barcode configuration at the end
      await updateBarcodeConfig();

      // Show completion message only if there were errors
      if (errorMessages.length > 0) {
        addInstanceMessage(
          `⚠️ تم إنهاء العملية مع ${errorMessages.length} خطأ(أخطاء). اطلع على تفاصيل الأخطاء للمزيد من المعلومات.`,
          'warning',
        );
      }
    } catch (error) {
      // This should rarely happen now as we handle errors in specific blocks
      console.error('Unexpected error in main process:', error);
      const errorText = await extractErrorMessage(error);
      const errorMsg = `✗ Unexpected error: ${errorText}`;
      addInstanceMessage(errorMsg, 'error');
    } finally {
      setIsProcessing(false);
      setCurrentBatch(0);
      setTotalBatches(0);
    }
  }, [
    startUpdate,
    formValues,
    instanceId,
    instance,
    holdings,
    ky,
    addInstanceMessage,
    addHoldingsMessage,
    incrementCounter,
    getOptimalConfig,
    processHoldings,
    updateBarcodeConfig,
    extractErrorMessage,
    errorMessages,
    setErrorMessages,
  ]);

  // Calculate total operations using analysis functions
  useEffect(() => {
    if (!isEmpty(formValues) && !isEmpty(initialValues)) {
      // Use the same analysis functions
      const instanceHasChanges = analyzeInstanceChanges(
        initialValues,
        formValues,
      );
      const holdingsAnalysis = analyzeHoldingsChanges(
        initialValues,
        formValues,
      );
      const itemsAnalysis = analyzeItemsChanges(initialValues, formValues);

      setProcessesSummary([
        {
          category: 'instance',
          new: 0,
          updated: instanceHasChanges ? 1 : 0,
          deleted: 0,
        },
        {
          category: 'holdings',
          new: holdingsAnalysis.added.length,
          updated: holdingsAnalysis.modified.length,
          deleted: holdingsAnalysis.deleted.length,
        },
        {
          category: 'items',
          new: itemsAnalysis.added.length,
          updated: itemsAnalysis.modified.length,
          deleted: itemsAnalysis.deleted.length,
        },
      ]);

      const totalOps =
        (instanceHasChanges ? 1 : 0) +
        holdingsAnalysis.added.length +
        holdingsAnalysis.modified.length +
        holdingsAnalysis.deleted.length +
        itemsAnalysis.added.length +
        itemsAnalysis.modified.length +
        itemsAnalysis.deleted.length;
      setTotalProcesses(totalOps);
    }
  }, [
    formValues,
    initialValues,
    analyzeInstanceChanges,
    analyzeHoldingsChanges,
    analyzeItemsChanges,
  ]);

  // Check for version conflicts when modal opens
  useEffect(() => {
    if (open && !startUpdate && !isProcessing) {
      checkForVersionConflicts().then(setOptimisticLockError);
    }
  }, [open, startUpdate, isProcessing, checkForVersionConflicts]);

  // Execute process when update starts
  useEffect(() => {
    if (startUpdate && !isProcessing) {
      executeMainProcess();
    }
  }, [startUpdate]);

  return (
    <>
      <ErrorDetailsModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errors={errorMessages}
        contextData={instance}
        titleKey="ui-plugin-inventory-templates.updateProgress.errors.modalTitle"
        mode="edit"
      />
      <OnSubmitModal
        open={open}
        onClose={onClose}
        instance={instance}
        instanceId={instanceId}
        processCounter={processCounter}
        instanceProgressMsg={instanceProgressMsg}
        holdingsProgressMsg={holdingsProgressMsg}
        itemsProgressMsg={itemsProgressMsg}
        errorMessages={errorMessages}
        totalProcesses={totalProcesses}
        startUpdate={startUpdate}
        setStartUpdate={setStartUpdate}
        processesSammary={processesSummary}
        isProcessing={isProcessing}
        currentBatch={currentBatch}
        showErrors={showErrors}
        setShowErrors={setShowErrors}
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
        optimisticLockError={optimisticLockError}
      />
    </>
  );
}

OnSubmitProgressModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  instanceId: PropTypes.string,
  formValues: PropTypes.object,
  initialValues: PropTypes.object,
  instance: PropTypes.object,
  holdings: PropTypes.array,
  items: PropTypes.object,
};

OnSubmitProgressModal.defaultProps = {
  open: false,
  onSuccess: null,
  instanceId: '',
  formValues: {},
  initialValues: {},
  instance: {},
  holdings: [],
  items: {},
};

export default OnSubmitProgressModal;
